import { Avatar, AvatarImage } from "../ui/avatar";

/**
 * Brand-styled deterministic avatar with geometric pattern.
 * Each user gets a unique warm-colored avatar derived from their name.
 * Pattern: layered geometric shapes (circles, arcs, diamonds) + initials.
 */

const PALETTE = [
  ["#C0392B", "#E8958C"], // red + light red
  ["#A93226", "#D65548"], // dark red + medium red
  ["#8B2A20", "#C0392B"], // deep red + red
  ["#574F47", "#A09789"], // stone + light stone
  ["#362F2A", "#70685E"], // dark stone + muted
  ["#1F1A15", "#574F47"], // near-black + stone
  ["#357A50", "#7CD9B4"], // forest + light green
  ["#A46222", "#F2C47F"], // amber + light amber
  ["#2B7D87", "#7DD4DF"], // teal + light teal
  ["#601C15", "#C0392B"], // deep wine + red
  ["#4A3F38", "#A09789"], // warm brown + light
  ["#70685E", "#C8C0B2"], // muted + light muted
] as const;

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

interface UserAvatarProps {
  userName: string;
  profilePictureUrl?: string | null;
  className?: string;
}

export const UserAvatar = ({ userName, profilePictureUrl, className }: UserAvatarProps) => {
  // Defensive: callers build userName as `${firstName} ${lastName}`, which becomes the literal
  // "undefined undefined" when the user is cleared (e.g. during logout). Strip those tokens so the
  // avatar never renders "UU" / "undefined" initials or alt text, regardless of the caller.
  const safeName = (userName ?? "")
    .replace(/\b(?:undefined|null)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  if (profilePictureUrl) {
    return (
      <Avatar className={className}>
        <AvatarImage
          src={profilePictureUrl}
          alt={`${safeName} profile`.trim()}
          className="h-full w-full object-cover"
        />
      </Avatar>
    );
  }

  const h = hash(safeName || "?");
  const [bg, accent] = PALETTE[h % PALETTE.length];
  const parts = (safeName || "?").split(/\s+/);
  const initials = ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";

  // Deterministic pattern bits from hash
  const b0 = (h >> 1) & 1;
  const b1 = (h >> 2) & 1;
  const b2 = (h >> 3) & 1;
  const b3 = (h >> 4) & 1;
  const b4 = (h >> 5) & 1;
  const b5 = (h >> 6) & 1;
  const rot = ((h >> 7) % 6) * 60;

  return (
    <Avatar className={className}>
      <svg viewBox="0 0 80 80" className="h-full w-full">
        {/* Base */}
        <rect width="80" height="80" rx="40" fill={bg} />

        {/* Geometric layer 1: quarter circles in corners */}
        {b0 === 1 && <path d="M0 0 Q0 24 24 24 Q24 0 0 0Z" fill={accent} opacity="0.25" />}
        {b1 === 1 && <path d="M80 0 Q80 24 56 24 Q56 0 80 0Z" fill={accent} opacity="0.25" />}
        {b2 === 1 && <path d="M0 80 Q0 56 24 56 Q24 80 0 80Z" fill={accent} opacity="0.2" />}
        {b3 === 1 && <path d="M80 80 Q80 56 56 56 Q56 80 80 80Z" fill={accent} opacity="0.2" />}

        {/* Geometric layer 2: inner ring or diamond */}
        {b4 === 1 ? (
          <circle
            cx="40"
            cy="40"
            r="28"
            fill="none"
            stroke={accent}
            strokeWidth="1.5"
            opacity="0.3"
          />
        ) : (
          <rect
            x="40"
            y="14"
            width="26"
            height="26"
            rx="3"
            fill={accent}
            opacity="0.15"
            transform={`rotate(${45 + rot} 40 40)`}
          />
        )}

        {/* Geometric layer 3: accent dot or arc */}
        {b5 === 1 ? (
          <circle cx="40" cy="14" r="4" fill={accent} opacity="0.35" />
        ) : (
          <path
            d="M22 58 A26 26 0 0 1 58 58"
            fill="none"
            stroke={accent}
            strokeWidth="1.5"
            opacity="0.25"
          />
        )}

        {/* Subtle radial gradient overlay */}
        <defs>
          <radialGradient id={`uag-${h}`} cx="35%" cy="30%">
            <stop offset="0%" stopColor="white" stopOpacity="0.1" />
            <stop offset="100%" stopColor="black" stopOpacity="0.08" />
          </radialGradient>
        </defs>
        <rect width="80" height="80" rx="40" fill={`url(#uag-${h})`} />

        {/* Initials */}
        <text
          x="40"
          y="40"
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontFamily="'DM Sans', system-ui, sans-serif"
          fontWeight="700"
          fontSize="26"
          letterSpacing="1"
        >
          {initials}
        </text>
      </svg>
    </Avatar>
  );
};
